// Google Analytics 4 (gtag.js) integration.
//
// The Measurement ID is read from REACT_APP_GA_MEASUREMENT_ID (e.g. "G-XXXXXXXXXX").
// When it is unset — local/dev builds, or before you create the GA property — every
// function here no-ops, so nothing is loaded and no hits are sent.
//
// Because this is a single-page app, gtag's automatic page_view only fires on the very
// first load; we disable it (send_page_view:false) and send one explicitly on each route
// change (see component/AnalyticsTracker.tsx).

const GA_ID: string | undefined = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

// ── Meta (Facebook) Pixel ────────────────────────────────────────────────────
// The base pixel (fbq init) lives in public/index.html. Because this is an SPA, we
// send PageView on every route change from here (see AnalyticsTracker), and fire the
// signup conversion via trackPixelEvent("CompleteRegistration"). All no-op if the
// pixel script hasn't loaded.

/** Send a Meta Pixel PageView for an SPA route change. */
export function trackPixelPageView(): void {
  if (typeof window !== "undefined" && window.fbq) window.fbq("track", "PageView");
}

/** Send a standard Meta Pixel event, e.g. trackPixelEvent("CompleteRegistration"). */
export function trackPixelEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window !== "undefined" && window.fbq) window.fbq("track", name, params ?? {});
}

export function isAnalyticsEnabled(): boolean {
  return !!GA_ID && typeof window !== "undefined";
}

export function initGA(): void {
  if (initialized || !isAnalyticsEnabled()) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag pushes its own `arguments` object onto dataLayer, so keep the classic shape.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID as string, { send_page_view: false });
}

/** Send a page_view for an SPA route change. */
export function trackPageView(path: string): void {
  if (!isAnalyticsEnabled() || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** Send a custom event (e.g. trackEvent("assessment_submitted", { type: "exam" })). */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled() || !window.gtag) return;
  window.gtag("event", name, params ?? {});
}
